import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import { useTenantScope } from '../context/TenantScopeContext.jsx';
import { api } from '../api/client.js';
import { adminAPI, alertsAPI } from '../services/api';
import { formatCOPAllowZero } from '../utils/money.js';
import { RISK_LABELS, explainFlag } from './opsPlaybook.js';
import { capabilitiesFromUser } from '../utils/solutionCapabilities.js';
import './AdminDashboard.css';

function formatOpsFlagSummary(flags, max = 2) {
    if (!flags?.length) return '';
    return flags
        .slice(0, max)
        .map((f) => {
            if (typeof f === 'string') return f;
            if (f && typeof f === 'object') {
                return explainFlag(f).title;
            }
            return '';
        })
        .filter(Boolean)
        .join(' · ');
}

const ACTION_MODULES = [
    { key: 'estado-funcional', label: 'Estado Funcional', path: '/analytical-boards', icon: '🛡️', efOnly: true },
    { key: 'diagnostico', label: 'Diagnóstico y manadas', path: '/admin/diagnostico', icon: '📋' },
    { key: 'cid', label: 'Calculadora CID', path: '/calculator', icon: '💰' },
    { key: 'friction', label: 'Mapa de fricción', path: '/friction-map', icon: '🧩' },
    { key: 'epa', label: 'EPA', path: '/epa', icon: '🧘' },
    { key: 'alertas', label: 'Alertas', path: '/alerts', icon: '🚨' },
    { key: 'indicadores', label: 'Indicadores', path: '/indicators', icon: '📈' },
    { key: 'equipo', label: 'Base de personal', path: '/team', icon: '👥' },
    { key: 'usuarios', label: 'Usuarios', path: '/admin/users', icon: '🔑' },
    { key: 'mensajes', label: 'Mensajes', path: '/messages', icon: '💬' },
];

const SUPERADMIN_ACTIONS = [
    { key: 'ops', label: 'Panel operacional (TI)', path: '/admin/ops', icon: '🛰️' },
    { key: 'tenants', label: 'Empresas', path: '/admin/tenants', icon: '🏢' },
    { key: 'finance', label: 'Tablero financiero', path: '/admin/finance', icon: '💵' },
    { key: 'onboarding', label: 'Implementación', path: '/admin/onboarding', icon: '🏗️' },
];

function getHeatColor(score, { noSignal, noOperationalStaff } = {}) {
    if (noOperationalStaff || noSignal || score == null) return '#94a3b8';
    if (score > 85) return '#22c55e';
    if (score > 70) return '#84cc16';
    if (score > 55) return '#eab308';
    if (score > 40) return '#f97316';
    return '#ef4444';
}

function levelBadgeClass(level) {
    if (level === 'red' || level === 'rojo') return 'red';
    if (level === 'yellow' || level === 'amarillo') return 'yellow';
    if (level === 'green' || level === 'verde') return 'green';
    return 'gray';
}

export default function AdminDashboard() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const isSuperAdmin = user?.rol === 'superadmin';
    const caps = capabilitiesFromUser(user);
    const { tenantId, needsTenant } = useTenantScope();
    const visibleActions = ACTION_MODULES.filter((m) => !m.efOnly || caps.showEstadoFuncional);

    const [stats, setStats] = useState(null);
    const [heatmap, setHeatmap] = useState([]);
    const [supervisorsMap, setSupervisorsMap] = useState([]);
    const [alerts, setAlerts] = useState([]);
    const [trends, setTrends] = useState([]);
    const [loading, setLoading] = useState(true);
    const [opsBanner, setOpsBanner] = useState(null);

    const [drill, setDrill] = useState(null);
    const [areaDetail, setAreaDetail] = useState(null);
    const [areaLoading, setAreaLoading] = useState(false);

    const fetchAdminData = useCallback(async () => {
        try {
            const tasks = [
                adminAPI.getStats(),
                adminAPI.getHeatmap(),
                alertsAPI.getActive(),
                adminAPI.getTrends(),
                adminAPI.getSupervisorsMap(),
            ];
            // Salud del servidor: solo en vista global (sin empresa seleccionada)
            if (isSuperAdmin && !tenantId) {
                tasks.push(api.getOpsSnapshot().catch(() => null));
            }

            const results = await Promise.all(tasks);
            const [statsRes, heatRes, alertsRes, trendsRes, supRes, opsRes] = results;

            setStats(statsRes.data.data);
            setHeatmap(heatRes.data.data || []);
            setAlerts(alertsRes.data.data || []);
            setTrends(trendsRes.data.data || []);
            setSupervisorsMap(supRes.data.data || []);

            if (isSuperAdmin && opsRes) {
                const risk = opsRes.operational?.riskLevel || 'ok';
                setOpsBanner({
                    risk,
                    flags: opsRes.operational?.flags || [],
                    generatedAt: opsRes.generatedAt,
                });
            } else {
                setOpsBanner(null);
            }
        } catch (err) {
            console.error('Error loading admin dashboard:', err);
        } finally {
            setLoading(false);
        }
    }, [isSuperAdmin, tenantId]);

    useEffect(() => {
        if (needsTenant) {
            setLoading(false);
            return;
        }
        setLoading(true);
        fetchAdminData();
    }, [fetchAdminData, tenantId, needsTenant]);

    const loadAreaDetail = useCallback(async (areaId) => {
        setAreaLoading(true);
        try {
            const res = await adminAPI.getAreaSummary(areaId);
            setAreaDetail(res.data.data);
        } catch (err) {
            console.error(err);
            setAreaDetail(null);
        } finally {
            setAreaLoading(false);
        }
    }, []);

    const openAreaDrill = (area) => {
        setDrill({ type: 'area', area });
        loadAreaDetail(area.id);
    };

    const openWeekDrill = (week) => {
        setDrill({ type: 'week', week });
        setAreaDetail(null);
    };

    const closeDrill = () => {
        setDrill(null);
        setAreaDetail(null);
    };

    if (loading) return <div className="admin-dashboard"><p className="admin-loading">Cargando panel de gobierno…</p></div>;

    const opsFlagSummary = opsBanner ? formatOpsFlagSummary(opsBanner.flags) : '';

    const delta = stats?.wellnessDelta;
    const deltaLabel =
        delta == null
            ? null
            : delta >= 0
              ? `↑ ${delta} pts vs semana anterior`
              : `↓ ${Math.abs(delta)} pts vs semana anterior`;

    return (
        <div className="admin-dashboard">
            <header className="admin-header">
                <div>
                    <h1>Panel global ALINEA</h1>
                    {user?.tenantName && (
                        <p className="admin-tenant-line">
                            Empresa: <strong>{user.tenantName}</strong>
                        </p>
                    )}
                    <p className="admin-header-sub">
                        Centro de gobierno: prioriza áreas, actúa sobre alertas y conecta bienestar con costo de rotación.
                    </p>
                </div>
            </header>

            {isSuperAdmin && !tenantId && opsBanner && opsBanner.risk !== 'ok' && (
                <div className={`admin-ops-banner risk-${opsBanner.risk}`} role="alert">
                    <div>
                        <strong>Salud del servidor (plataforma)</strong>
                        <p>
                            Estado:{' '}
                            <span className="admin-ops-risk">
                                {RISK_LABELS[opsBanner.risk]?.title || opsBanner.risk}
                            </span>
                            {opsFlagSummary ? ` — ${opsFlagSummary}` : ''}
                        </p>
                        <p className="admin-ops-banner-hint text-xs text-muted">
                            No es el clima laboral de la empresa. Detalle y qué hacer en el panel operacional.
                        </p>
                    </div>
                    <button type="button" className="btn-outline" onClick={() => navigate('/admin/ops')}>
                        Ver panel TI
                    </button>
                </div>
            )}

            <section className="admin-actions-hub glass-card" aria-label="Acciones y módulos">
                <h3>Acciones rápidas</h3>
                <div className="admin-actions-grid">
                    {visibleActions.map((m) => (
                        <button
                            key={m.key}
                            type="button"
                            className="admin-action-tile"
                            onClick={() => navigate(m.path)}
                        >
                            <span className="admin-action-icon" aria-hidden>{m.icon}</span>
                            <span>{m.label}</span>
                        </button>
                    ))}
                    <button
                        type="button"
                        className="admin-action-tile accent"
                        onClick={() => navigate('/calculator')}
                    >
                        <span className="admin-action-icon" aria-hidden>🧮</span>
                        <span>Simular rotación (CID)</span>
                    </button>
                </div>
                {isSuperAdmin && (
                    <>
                        <p className="admin-actions-kicker">Solo súper administrador</p>
                        <div className="admin-actions-grid superadmin-only">
                            {SUPERADMIN_ACTIONS.map((m) => (
                                <button
                                    key={m.key}
                                    type="button"
                                    className="admin-action-tile muted"
                                    onClick={() => navigate(m.path)}
                                >
                                    <span className="admin-action-icon" aria-hidden>{m.icon}</span>
                                    <span>{m.label}</span>
                                </button>
                            ))}
                        </div>
                    </>
                )}
            </section>

            <section className="metrics-grid" aria-label="Métricas globales">
                <button
                    type="button"
                    className="metric-card metric-card-click"
                    onClick={() => navigate('/indicators')}
                >
                    <span className="metric-label">Bienestar general (7 días)</span>
                    <div className="metric-value-row">
                        <span className="metric-value">{stats?.wellness ?? 0}%</span>
                        {deltaLabel && (
                            <span className={`metric-trend ${delta >= 0 ? 'pos' : 'neg'}`}>{deltaLabel}</span>
                        )}
                    </div>
                    <div className="progress-bar-bg">
                        <div className="progress-bar-fill" style={{ width: `${stats?.wellness || 0}%` }} />
                    </div>
                    <span className="metric-hint">Ver indicadores →</span>
                </button>

                <button
                    type="button"
                    className="metric-card metric-card-click"
                    onClick={() => navigate('/alerts?level=red')}
                >
                    <span className="metric-label">Alertas activas</span>
                    <div className="metric-value-row">
                        <div className="alert-mini-box red">
                            <span className="val">{stats?.alerts?.rojas || 0}</span>
                            <span className="lab">Críticas</span>
                        </div>
                        <div className="alert-mini-box yellow">
                            <span className="val">{stats?.alerts?.amarillas || 0}</span>
                            <span className="lab">Medias</span>
                        </div>
                    </div>
                    <span className="metric-hint">Gestionar alertas →</span>
                </button>

                <button
                    type="button"
                    className="metric-card highlight metric-card-click"
                    onClick={() => navigate('/calculator')}
                >
                    <span className="metric-label">Costo por rotación (YTD)</span>
                    <div className="metric-value-row">
                        <span className="metric-value">{formatCOPAllowZero(stats?.cidTotal || 0)}</span>
                    </div>
                    <span className="metric-sub">
                        {stats?.cidEventsYtd || 0} evento(s) registrado(s) · Configurar / simular CID →
                    </span>
                </button>
            </section>

            <div className="admin-main-grid">
                <section className="heatmap-section glass-card">
                    <h3>Mapa de calor — priorizar por área</h3>
                    <p className="section-hint">
                        Toca un área para ver detalle. Admin/superadmin no entran en el score; sin check-in se
                        muestra «Sin señal» (no 50% naranja).
                    </p>
                    <div className="heatmap-grid">
                        {heatmap.length === 0 ? (
                            <p className="text-muted">Sin áreas configuradas.</p>
                        ) : (
                            heatmap.map((area) => (
                                <button
                                    key={area.id}
                                    type="button"
                                    className={`heatmap-cell${drill?.type === 'area' && drill.area?.id === area.id ? ' on' : ''}`}
                                    style={{
                                        backgroundColor: getHeatColor(area.score, {
                                            noSignal: area.noSignal,
                                            noOperationalStaff: area.noOperationalStaff,
                                        }),
                                    }}
                                    onClick={() => openAreaDrill(area)}
                                >
                                    <span className="area-name">{area.nombre}</span>
                                    <span className="area-score">
                                        {area.noOperationalStaff
                                            ? 'Sin personal'
                                            : area.noSignal || area.score == null
                                              ? 'Sin señal'
                                              : `${area.score}%`}
                                    </span>
                                    {(area.alerts?.red > 0 || area.alerts?.yellow > 0) && (
                                        <span className="area-alerts-pill">
                                            {area.alerts.red > 0 && `🚨 ${area.alerts.red}`}
                                            {area.alerts.yellow > 0 && ` ⚠ ${area.alerts.yellow}`}
                                        </span>
                                    )}
                                </button>
                            ))
                        )}
                    </div>
                    <div className="heatmap-legend">
                        <span>Crítico</span>
                        <div className="legend-gradient" />
                        <span>Excelente</span>
                    </div>
                </section>

                <section className="trends-section glass-card">
                    <h3>Tendencia: bienestar vs rotación</h3>
                    <p className="section-hint">Toca una semana para ver el detalle.</p>
                    <div className="simple-chart">
                        {trends.map((t) => (
                            <button
                                key={t.weekStart}
                                type="button"
                                className={`chart-col${drill?.type === 'week' && drill.week?.weekStart === t.weekStart ? ' on' : ''}`}
                                onClick={() => openWeekDrill(t)}
                            >
                                <div className="bar-group">
                                    <div className="bar wellness" style={{ height: `${t.bienestar}%` }} title={`Bienestar ${t.bienestar}%`} />
                                    <div className="bar turnover" style={{ height: `${Math.min(100, t.rotacion * 5)}%` }} title={`Rotación ${t.rotacionEventos} evt.`} />
                                </div>
                                <span className="chart-label">{t.semana}</span>
                            </button>
                        ))}
                    </div>
                    <div className="chart-legend">
                        <span className="dot wellness" /> Bienestar
                        <span className="dot turnover" /> Rotación (eventos)
                    </div>
                </section>
            </div>

            {drill && (
                <section className="admin-drill-panel glass-card" role="region" aria-label="Detalle seleccionado">
                    <div className="admin-drill-head">
                        <h3>
                            {drill.type === 'area' ? `Área: ${drill.area.nombre}` : `Semana: ${drill.week.semana}`}
                        </h3>
                        <button type="button" className="btn-small" onClick={closeDrill}>
                            Cerrar
                        </button>
                    </div>

                    {drill.type === 'week' && (
                        <ul className="admin-drill-list">
                            <li><span>Bienestar medio</span><strong>{drill.week.bienestar}%</strong></li>
                            <li><span>Check-ins registrados</span><strong>{drill.week.checkins}</strong></li>
                            <li><span>Eventos de rotación</span><strong>{drill.week.rotacionEventos}</strong></li>
                            <li><span>Impacto CID esa semana</span><strong>{formatCOPAllowZero(drill.week.rotacionCosto)}</strong></li>
                        </ul>
                    )}

                    {drill.type === 'area' && (
                        <>
                            {areaLoading && <p className="text-muted">Cargando detalle…</p>}
                            {!areaLoading && areaDetail && (
                                <>
                                    <ul className="admin-drill-list">
                                        <li><span>Bienestar (7 días)</span><strong>{areaDetail.score}%</strong></li>
                                        <li><span>Personas activas</span><strong>{areaDetail.headcount}</strong></li>
                                        <li><span>Check-ins (7 días)</span><strong>{areaDetail.checkins7d}</strong></li>
                                        <li>
                                            <span>Alertas activas</span>
                                            <strong>
                                                🚨 {areaDetail.alerts.red} · ⚠ {areaDetail.alerts.yellow}
                                            </strong>
                                        </li>
                                    </ul>
                                    {areaDetail.topAlerts?.length > 0 && (
                                        <div className="admin-drill-alerts">
                                            <p className="admin-drill-kicker">Alertas prioritarias</p>
                                            <ul>
                                                {areaDetail.topAlerts.map((a) => (
                                                    <li key={a.id}>
                                                        <button
                                                            type="button"
                                                            className="admin-drill-link"
                                                            onClick={() => navigate(`/team/${a.employee_id}`)}
                                                        >
                                                            {a.employee_name} — {a.title || a.alert_code}
                                                        </button>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    <div className="admin-drill-actions">
                                        <button
                                            type="button"
                                            className="btn-outline"
                                            onClick={() => navigate(`/team?areaId=${areaDetail.area.id}`)}
                                        >
                                            Ver personal del área
                                        </button>
                                        <button
                                            type="button"
                                            className="btn-outline"
                                            onClick={() => navigate('/alerts')}
                                        >
                                            Gestionar alertas
                                        </button>
                                        <button
                                            type="button"
                                            className="btn-outline"
                                            onClick={() => navigate('/calculator')}
                                        >
                                            Simular impacto CID
                                        </button>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </section>
            )}

            <section className="glass-card admin-supervisors-section">
                <h3>Estado de manadas por líder</h3>
                <p className="section-hint">Toca un líder para ver su equipo en la base de personal.</p>
                <div className="admin-supervisors-grid">
                    {supervisorsMap.map((sup) => (
                        <button
                            key={sup.id}
                            type="button"
                            className="admin-supervisor-card"
                            onClick={() => navigate(`/team?supervisorId=${sup.id}`)}
                        >
                            <div className="admin-supervisor-top">
                                <div>
                                    <h4>{sup.name}</h4>
                                    <span className="text-muted text-sm">Manada: {sup.teamSize} personas</span>
                                </div>
                                <span className={`badge-level ${levelBadgeClass(sup.level)}`}>{sup.label}</span>
                            </div>
                            <div className="progress-bar-bg admin-sup-bar">
                                <div
                                    className="progress-bar-fill"
                                    style={{ width: `${sup.score || 0}%`, backgroundColor: getHeatColor(sup.score || 0) }}
                                />
                            </div>
                            <p className="admin-sup-rec"><strong>Plan:</strong> {sup.recommendation}</p>
                        </button>
                    ))}
                </div>
            </section>

            <section className="alerts-table-section glass-card">
                <h3>Alertas activas — acción inmediata</h3>
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>Colaborador</th>
                            <th>Área</th>
                            <th>Nivel</th>
                            <th>Motivo</th>
                            <th>Acción</th>
                        </tr>
                    </thead>
                    <tbody>
                        {alerts.length === 0 ? (
                            <tr>
                                <td colSpan={5} className="text-muted">Sin alertas activas.</td>
                            </tr>
                        ) : (
                            alerts.map((a) => (
                                <tr key={a.id}>
                                    <td>{a.trabajador_nombre}</td>
                                    <td>{a.area_nombre || '—'}</td>
                                    <td>
                                        <span className={`badge-level ${levelBadgeClass(a.level)}`}>
                                            {a.level === 'red' ? 'Crítica' : a.level === 'yellow' ? 'Media' : 'Info'}
                                        </span>
                                    </td>
                                    <td>{a.rule_code || a.alert_code}</td>
                                    <td>
                                        <button
                                            type="button"
                                            className="btn-small"
                                            onClick={() =>
                                                a.employee_id
                                                    ? navigate(`/team/${a.employee_id}`)
                                                    : navigate('/alerts')
                                            }
                                        >
                                            Ver ficha
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </section>
        </div>
    );
}
